import { __assign, __awaiter, __generator } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useState, useMemo, } from "react";
import { usePangeaListRequest, } from "@pangeacyber/react-mui-shared";
import AlertsSnackbar from "../components/AlertSnackbar";
import { alertOnError } from "../components/AlertSnackbar/hooks";
import { useBucketAwareApiRef } from "./utils";
var DEFAULT_LIST_RESPONSE = {
    count: 0,
    last: "",
    objects: [],
};
var DEFAULT_LIST_REQUEST_PROPS = {
    page: 0,
    setPage: function () { },
    bucketId: "",
    setBucketId: function () { },
    pageSize: 0,
    setPageSize: function () { },
    last: undefined,
    setCurrentLast: function () { },
    lastKnownPage: 0,
    sorting: {},
    setSorting: function () { },
    filters: {},
    setFilters: function () { },
    body: {},
};
var StoreFileViewerContext = createContext({
    apiRef: {
        list: undefined,
        get: undefined,
    },
    data: DEFAULT_LIST_RESPONSE,
    error: undefined,
    reload: function () { },
    loading: false,
    request: DEFAULT_LIST_REQUEST_PROPS,
    bucketId: undefined,
    setBucketId: function () { },
    previewId: undefined,
    setPreviewId: function () { },
    parent: undefined,
    updated: 0,
    triggerUpdate: function () { },
});
var StoreFileViewerProvider = function (_a) {
    var _b, _c, _d;
    var children = _a.children, apiRefProp = _a.apiRef, configurations = _a.configurations, defaultFilter = _a.defaultFilter, defaultSort = _a.defaultSort, defaultSortBy = _a.defaultSortBy, defaultShareLinkTitle = _a.defaultShareLinkTitle;
    var _e = useState(), bucketId = _e[0], setBucketId = _e[1];
    var _f = useState(false), loading = _f[0], setLoading = _f[1];
    var apiRef = useBucketAwareApiRef(apiRefProp, bucketId);
    var defaultFilterWithFolder = useMemo(function () {
        return __assign({ folder: "/" }, defaultFilter);
    }, [defaultFilter]);
    var request = usePangeaListRequest({
        defaultFilter: defaultFilterWithFolder,
        defaultSort: defaultSort,
        defaultSortBy: defaultSortBy,
    }, {
        parent_id: {
            key: "parent_id",
            ignoreFalsy: true,
        },
        folder: {
            key: "folder",
        },
    });
    var _g = useState(0), updated = _g[0], setUpdated = _g[1];
    var triggerUpdate = useCallback(function () {
        setUpdated(function (state) { return state + 1; });
    }, [setUpdated]);
    var _h = useState(), error = _h[0], setError = _h[1];
    var _j = useState(DEFAULT_LIST_RESPONSE), data = _j[0], setData = _j[1];
    var _k = useState(), previewId = _k[0], setPreviewId = _k[1];
    var _l = useState(), parent = _l[0], setParent = _l[1];
    var search = function () {
        if (!(apiRef === null || apiRef === void 0 ? void 0 : apiRef.list))
            return;
        setLoading(true);
        apiRef
            .list(request.body)
            .then(function (response) {
            if (response.status === "Success") {
                setData(response.result);
                request.setCurrentLast(response.result.last);
            }
            else
                setError(response);
            setLoading(false);
        })
            .catch(function (error) {
            setError(error);
            setLoading(false);
        });
    };
    useEffect(function () {
        if (!!request.filters.parent_id && !!apiRef.list) {
            setLoading(true);
            apiRef
                .list({ filter: { id: request.filters.parent_id } })
                .then(function (response) {
                var _a, _b, _c;
                if (response.status === "Success" &&
                    ((_b = (_a = response === null || response === void 0 ? void 0 : response.result) === null || _a === void 0 ? void 0 : _a.objects) === null || _b === void 0 ? void 0 : _b.length))
                    setParent((_c = response.result) === null || _c === void 0 ? void 0 : _c.objects[0]);
                else {
                    setError(response);
                    setParent(undefined);
                }
                setLoading(false);
            })
                .catch(function (error) {
                setError(error);
                setParent(undefined);
                setLoading(false);
            });
        }
        else if (!!request.filters.folder && !!apiRef.list) {
            var folder = request.filters.folder;
            var parts = folder.split("/").filter(function (p) { return !!p; });
            if (parts.length >= 1) {
                var name_1 = parts.pop();
                setLoading(true);
                apiRef
                    .list({
                    filter: { folder: "/".concat(parts.join("/")), name: name_1, type: "folder" },
                })
                    .then(function (response) {
                    var _a, _b, _c;
                    if (response.status === "Success" &&
                        ((_b = (_a = response === null || response === void 0 ? void 0 : response.result) === null || _a === void 0 ? void 0 : _a.objects) === null || _b === void 0 ? void 0 : _b.length))
                        setParent((_c = response.result) === null || _c === void 0 ? void 0 : _c.objects[0]);
                    else {
                        setError(response);
                        setParent(undefined);
                    }
                    setLoading(false);
                })
                    .catch(function (error) {
                    setError(error);
                    setParent(undefined);
                    setLoading(false);
                });
            }
            else {
                setParent(undefined);
            }
        }
        else {
            setParent(undefined);
        }
    }, [apiRef.list, request.filters.parent_id, request.filters.folder]);
    useEffect(function () {
        search();
        return function () {
            setLoading(false);
        };
    }, [apiRef.list, request.body]);
    return (_jsxs(StoreFileViewerContext.Provider, { value: {
            apiRef: apiRef,
            configurations: configurations,
            data: data,
            error: error,
            bucketId: bucketId,
            setBucketId: setBucketId,
            reload: search,
            loading: loading && !((_b = data === null || data === void 0 ? void 0 : data.objects) === null || _b === void 0 ? void 0 : _b.length),
            request: request,
            previewId: previewId,
            setPreviewId: setPreviewId,
            parent: parent,
            updated: updated,
            triggerUpdate: triggerUpdate,
            defaultShareLinkTitle: defaultShareLinkTitle,
        }, children: [children, !!((_c = configurations === null || configurations === void 0 ? void 0 : configurations.alerts) === null || _c === void 0 ? void 0 : _c.displayAlertOnError) && (_jsx(AlertsSnackbar, __assign({}, (_d = configurations === null || configurations === void 0 ? void 0 : configurations.alerts) === null || _d === void 0 ? void 0 : _d.AlertSnackbarProps)))] }));
};
export var useStoreFileViewerFolder = function () {
    var _a;
    var _b = useStoreFileViewerContext(), request = _b.request, parent = _b.parent;
    var setFolder = useCallback(function (folder) {
        request.setPage(1);
        request.setFilters({ folder: folder });
    }, [request.setFilters]);
    var setParentId = useCallback(function (parentId) {
        request.setPage(1);
        if (!parentId)
            return request.setFilters({
                parent_id: "",
            });
        request.setFilters({ parent_id: parentId });
    }, [request.setFilters, parent === null || parent === void 0 ? void 0 : parent.id]);
    return {
        parent: parent,
        folder: ((_a = parent === null || parent === void 0 ? void 0 : parent.path) !== null && _a !== void 0 ? _a : !!parent)
            ? "".concat(parent === null || parent === void 0 ? void 0 : parent.folder, "/").concat(parent === null || parent === void 0 ? void 0 : parent.name, "/").replace("//", "/")
            : "/",
        setFolder: setFolder,
        setParentId: setParentId,
    };
};
export var useStoreFileViewerBuckets = function () {
    var _a = useStoreFileViewerContext(), apiRef = _a.apiRef, bucketId = _a.bucketId, setBucketId = _a.setBucketId, request = _a.request;
    var setBucketId_ = useCallback(function (updates) {
        request.setFilters({
            folder: "/",
        });
        return setBucketId(updates);
    }, [setBucketId]);
    var _b = useState([]), buckets = _b[0], setBuckets = _b[1];
    var handleFetchBuckets = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (!apiRef.buckets) {
                setBuckets([]);
                return [2 /*return*/];
            }
            return [2 /*return*/, apiRef
                    .buckets({})
                    .then(function (response) {
                    var _a, _b;
                    // Instead we need to prompt open the modal
                    var buckets = (_b = (_a = response === null || response === void 0 ? void 0 : response.result) === null || _a === void 0 ? void 0 : _a.buckets) !== null && _b !== void 0 ? _b : [];
                    if (buckets.length) {
                        setBuckets(buckets);
                    }
                })
                    .catch(function (err) {
                    alertOnError(err);
                    return false;
                })];
        });
    }); };
    useEffect(function () {
        handleFetchBuckets();
    }, [apiRef === null || apiRef === void 0 ? void 0 : apiRef.buckets]);
    return {
        bucketId: bucketId,
        setBucketId: setBucketId_,
        buckets: buckets,
    };
};
var CreateShareContext = createContext({
    contentType: "",
    shareType: "",
    loading: false,
    errors: undefined,
    password: "",
    sent: false,
    shareLink: undefined,
    setLoading: function () { },
    setSent: function () { },
    setErrors: function () { },
    setPassword: function () { },
    setShareLink: function () { },
    resetContext: function () { },
});
export var ShareCreateProvider = function (_a) {
    var contentType = _a.contentType, shareType = _a.shareType, children = _a.children;
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    var _c = useState(false), sent = _c[0], setSent = _c[1];
    var _d = useState({}), errors = _d[0], setErrors = _d[1];
    var _e = useState(""), password = _e[0], setPassword = _e[1];
    var _f = useState(undefined), shareLink = _f[0], setShareLink = _f[1];
    var resetContext = function () {
        setLoading(false);
        setSent(false);
        setErrors({});
        setPassword("");
        setShareLink(undefined);
    };
    return (_jsx(CreateShareContext.Provider, { value: {
            contentType: contentType,
            shareType: shareType,
            loading: loading,
            sent: sent,
            errors: errors,
            password: password,
            shareLink: shareLink,
            setLoading: setLoading,
            setSent: setSent,
            setErrors: setErrors,
            setPassword: setPassword,
            setShareLink: setShareLink,
            resetContext: resetContext,
        }, children: children }));
};
export var useStoreFileViewerPreview = function () {
    var _a = useStoreFileViewerContext(), previewId = _a.previewId, setPreviewId = _a.setPreviewId;
    return [previewId, setPreviewId];
};
export var useStoreFileViewerContext = function () {
    return useContext(StoreFileViewerContext);
};
export var useStoreViewerData = function () {
    var apiRef = useStoreFileViewerContext().apiRef;
};
export var useCreateShareContext = function () {
    return useContext(CreateShareContext);
};
export default StoreFileViewerProvider;
